import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Phone, KeyRound, ArrowRight, ArrowLeft, Lock, MessageSquare, MessageCircle, User, Gift, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LangToggle from "@/components/LangToggle";
import { TermsModal, PrivacyModal } from "@/components/LegalDocs";

const ROLE_TABS = [
  { key: "customer", labelKey: "customer", color: "bg-tenant-primary" },
  { key: "agent", labelKey: "agent", color: "bg-emerald-600" },
  { key: "tenant_admin", labelKey: "admin", color: "bg-violet-600" },
];

const RESEND_COOLDOWN = 30;

export default function Login() {
  const { sendOtp, login, tenant } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [role, setRole] = useState("customer");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  // step: 1 (phone) | 2 (otp) | 3 (registration: name + ref + T&C)
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");
  const [channel, setChannel] = useState("sms");
  const [resendLeft, setResendLeft] = useState(0);
  const timerRef = useRef(null);

  // Registration step state
  const [fullName, setFullName] = useState("");
  // Read referral from URL synchronously to avoid races with the AuthProvider effect
  const refFromUrl = (() => {
    try {
      const v = new URLSearchParams(window.location.search).get("ref");
      return v ? v.toUpperCase().replace(/[^A-Z0-9]/g, "") : "";
    } catch { return ""; }
  })();
  const initialRef = refFromUrl || localStorage.getItem("fa_ref_code") || "";
  if (refFromUrl) localStorage.setItem("fa_ref_code", refFromUrl);
  const [hasReferral, setHasReferral] = useState(!!initialRef);
  const [referralCode, setReferralCode] = useState(initialRef);
  const [agreed, setAgreed] = useState(true); // default checked per spec
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const referralFromLink = !!initialRef; // locked when present

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startResendTimer = () => {
    setResendLeft(RESEND_COOLDOWN);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const slugLocked = !!localStorage.getItem("fa_slug") && tenant?.id;

  const onSendOtp = async (isResend = false) => {
    if (!phone || phone.length < 10) {
      toast.error("Please enter valid mobile number");
      return;
    }
    if (isResend && resendLeft > 0) return;
    const effectiveRole = phone === "9858558555" ? "super_admin" : role;
    setLoading(true);
    try {
      const r = await sendOtp(phone, effectiveRole, undefined, channel);
      setStep(2);
      startResendTimer();
      if (r.dev_otp) {
        setHint(`${r.fixed ? "Fixed" : "Dev"} OTP: ${r.dev_otp}`);
        toast.success(`OTP sent via ${(r.channel || channel).toUpperCase()}. (Dev: ${r.dev_otp})`);
      } else {
        setHint(`OTP sent via ${(r.channel || channel).toUpperCase()} — check your phone`);
        toast.success(`OTP sent via ${(r.channel || channel).toUpperCase()}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async (extras = {}) => {
    const effectiveRole = phone === "9858558555" ? "super_admin" : role;
    setLoading(true);
    try {
      const result = await login(phone, otp, effectiveRole, undefined, extras);
      if (result?.needs_registration) {
        setStep(3);
        return;
      }
      // Successful login => user object
      const u = result;
      // Clear consumed referral code link param
      localStorage.removeItem("fa_ref_code");
      toast.success(`Welcome, ${u.full_name}!`);
      const home = {
        super_admin: "/super-admin", tenant_admin: "/admin",
        agent: "/agent", customer: "/customer",
      }[u.role] || "/";
      nav(home, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async () => {
    if (!otp || otp.length < 4) return toast.error("Enter the OTP");
    await completeLogin();
  };

  const onCompleteRegistration = async () => {
    if (!fullName.trim()) return toast.error("Please enter your full name");
    if (!agreed) return toast.error("Please accept the Terms & Conditions");
    await completeLogin({
      full_name: fullName.trim(),
      referral_code: hasReferral ? referralCode.trim().toUpperCase() : "",
      agreed_to_terms: true,
    });
  };

  const quickFill = (p) => {
    setPhone(p);
    setStep(1);
    setOtp("");
  };

  return (
    <div className="mobile-frame bg-tenant-gradient min-h-screen text-white relative overflow-hidden" data-testid="login-screen">
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 0%, rgba(212,175,55,0.45), transparent 45%)" }} />

      <div className="relative z-10 px-5 sm:px-6 pt-6 sm:pt-8 pb-4 sm:pb-6 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl gold-grad grid place-items-center text-tenant-primary overflow-hidden">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={tenant.brand_name} className="w-full h-full object-cover" />
            ) : (
              <ShieldCheck className="w-5 h-5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-extrabold text-[13px] sm:text-sm tracking-tight truncate">{tenant?.brand_name || t("app_name")}</div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-tenant-accent font-bold">{t("tagline")}</div>
          </div>
        </div>
        <LangToggle />
      </div>

      {slugLocked && (
        <div className="relative z-10 mx-5 sm:mx-6 mt-1 mb-1 bg-white/15 backdrop-blur-md rounded-xl px-3 py-1.5 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/90" data-testid="slug-locked-badge">
          <Lock className="w-3 h-3" /> {t("logging_into")} {tenant?.brand_name}
        </div>
      )}

      <div className="relative z-10 px-5 sm:px-6 mt-4 sm:mt-6">
        <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">{t("welcome_back")} 👋</h2>
        <p className="text-xs sm:text-sm opacity-80 mt-1">{t("login_subtitle")}</p>
      </div>

      <div className="relative z-10 px-5 sm:px-6 mt-4 sm:mt-6">
        <div className="grid grid-cols-3 gap-1.5 bg-white/10 backdrop-blur-md rounded-2xl p-1.5">
          {ROLE_TABS.map((r) => (
            <button
              key={r.key}
              data-testid={`login-role-${r.key}`}
              onClick={() => { setRole(r.key); setStep(1); setOtp(""); }}
              className={`py-2 sm:py-2.5 rounded-xl text-[10px] sm:text-[11px] font-bold tap-scale uppercase tracking-wider ${
                role === r.key ? "bg-white text-tenant-primary shadow-lg" : "text-white/80"
              }`}
            >
              {t(r.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="relative z-10 mt-5 sm:mt-6 mx-5 sm:mx-6 bg-white text-slate-900 rounded-3xl p-5 sm:p-6 card-shadow-lg">
        {step === 1 && (
          <>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{t("mobile_number")}</label>
            <div className="mt-2 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-3 focus-within:border-tenant-primary">
              <Phone className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500 font-semibold">+91</span>
              <input
                data-testid="login-phone-input"
                inputMode="numeric"
                maxLength={10}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder={t("phone_placeholder")}
                className="flex-1 outline-none text-base min-w-0"
              />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                data-testid="channel-sms"
                onClick={() => setChannel("sms")}
                className={`py-2 rounded-xl text-[11px] font-bold inline-flex items-center justify-center gap-1.5 ${channel === "sms" ? "bg-tenant-primary text-white" : "bg-slate-100 text-slate-500"}`}
              >
                <MessageSquare className="w-3.5 h-3.5" /> SMS
              </button>
              <button
                data-testid="channel-whatsapp"
                onClick={() => setChannel("whatsapp")}
                className={`py-2 rounded-xl text-[11px] font-bold inline-flex items-center justify-center gap-1.5 ${channel === "whatsapp" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
            </div>

            <button
              data-testid="login-send-otp-btn"
              onClick={() => onSendOtp(false)}
              disabled={loading}
              className="mt-4 w-full bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? t("sending") : t("send_otp")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {step === 2 && (
          <>
            <button data-testid="login-back-btn" onClick={() => setStep(1)} className="text-xs text-slate-500 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> {t("change_number")}
            </button>
            <div className="mt-3">
              <div className="text-sm">{t("otp_sent_to")} <span className="font-bold">+91 {phone}</span></div>
              {hint && <div className="text-[11px] text-emerald-600 font-bold mt-1">{hint}</div>}
            </div>
            <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{t("enter_otp")}</label>
            <div className="mt-2 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-3 focus-within:border-tenant-primary">
              <KeyRound className="w-4 h-4 text-slate-400" />
              <input
                data-testid="login-otp-input"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder={t("otp_placeholder")}
                className="flex-1 outline-none text-base tracking-[0.4em] font-bold min-w-0"
              />
            </div>
            <button
              data-testid="login-verify-btn"
              onClick={onVerify}
              disabled={loading}
              className="mt-5 w-full bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? t("verifying") : t("verify_otp")}
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="mt-3 text-center">
              {resendLeft > 0 ? (
                <span className="text-[11px] text-slate-400 font-bold" data-testid="resend-cooldown">{t("resend_in")} {resendLeft}s</span>
              ) : (
                <button data-testid="login-resend-btn" onClick={() => onSendOtp(true)} className="text-[11px] font-bold text-tenant-primary underline">{t("resend_otp")}</button>
              )}
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <button data-testid="reg-back-btn" onClick={() => setStep(2)} className="text-xs text-slate-500 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> {t("back")}
            </button>
            <h3 className="text-lg font-extrabold tracking-tight mt-2">{t("almost_there")}</h3>
            <p className="text-xs text-slate-500">{t("almost_there_desc")}</p>

            <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{t("full_name")}</label>
            <div className="mt-2 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-3 focus-within:border-tenant-primary">
              <User className="w-4 h-4 text-slate-400" />
              <input
                data-testid="register-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t("enter_full_name")}
                className="flex-1 outline-none text-base min-w-0"
                autoFocus
              />
            </div>

            <div className="mt-3 flex items-center gap-2">
              <input
                id="referral-toggle"
                data-testid="referral-toggle"
                type="checkbox"
                checked={hasReferral}
                disabled={referralFromLink}
                onChange={(e) => setHasReferral(e.target.checked)}
                className="w-4 h-4 accent-tenant-primary"
              />
              <label htmlFor="referral-toggle" className="text-[12px] font-bold text-slate-700 inline-flex items-center gap-1">
                <Gift className="w-3.5 h-3.5 text-tenant-accent" /> {t("i_have_referral")}
              </label>
            </div>

            {hasReferral && (
              <div className="mt-2">
                <div className="flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-2.5 focus-within:border-tenant-primary bg-white">
                  <Gift className="w-4 h-4 text-tenant-accent" />
                  <input
                    data-testid="referral-code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    placeholder={t("enter_referral_code")}
                    readOnly={referralFromLink}
                    className={`flex-1 outline-none font-mono tracking-widest font-bold text-sm ${referralFromLink ? "bg-slate-50 cursor-not-allowed" : ""}`}
                  />
                  {referralFromLink && <Lock className="w-3.5 h-3.5 text-slate-400" title="From referral link" />}
                </div>
                {referralFromLink && (
                  <div className="text-[10px] text-emerald-600 mt-1 font-bold">{t("referral_locked_msg")}</div>
                )}
              </div>
            )}

            <div className="mt-4 flex items-start gap-2 bg-slate-50 rounded-2xl p-3">
              <input
                id="agree-tnc"
                data-testid="agree-tnc"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="w-4 h-4 accent-tenant-primary mt-0.5"
              />
              <label htmlFor="agree-tnc" className="text-[11px] text-slate-600 leading-relaxed">
                {t("i_agree_to")}{" "}
                <button onClick={() => setShowTerms(true)} className="font-bold text-tenant-primary underline" data-testid="open-terms">{t("terms_conditions")}</button>
                {" & "}
                <button onClick={() => setShowPrivacy(true)} className="font-bold text-tenant-primary underline" data-testid="open-privacy">{t("privacy_policy")}</button>.
              </label>
            </div>

            <button
              data-testid="register-submit"
              onClick={onCompleteRegistration}
              disabled={loading || !agreed || !fullName.trim()}
              className="mt-5 w-full bg-emerald-600 text-white py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? t("creating_account") : t("create_account_btn")}
              <Check className="w-4 h-4" />
            </button>
          </>
        )}

        <div className="mt-5 sm:mt-6 pt-4 border-t border-slate-100 text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">{t("demo_accounts")}</div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <button data-testid="demo-tenant" onClick={() => { setRole("tenant_admin"); quickFill("9000000001"); }} className="text-[11px] bg-slate-100 hover:bg-slate-200 rounded-xl py-2 font-semibold">
              🏢 {t("demo_tenant")}
            </button>
            <button data-testid="demo-agent" onClick={() => { setRole("agent"); quickFill("9000000010"); }} className="text-[11px] bg-slate-100 hover:bg-slate-200 rounded-xl py-2 font-semibold">
              🧑‍💼 {t("demo_agent")}
            </button>
            <button data-testid="demo-customer" onClick={() => { setRole("customer"); quickFill("9000000020"); }} className="text-[11px] bg-slate-100 hover:bg-slate-200 rounded-xl py-2 font-semibold">
              👤 {t("demo_customer")}
            </button>
          </div>
        </div>
      </div>

      <div className="relative z-10 px-5 sm:px-6 mt-5 sm:mt-6 mb-6 sm:mb-8 text-center">
        <div className="text-sm opacity-80">{t("no_account")}{" "}
          <button data-testid="goto-register-btn" onClick={() => nav("/register")} className="text-tenant-accent font-bold">{t("register")}</button>
        </div>
      </div>

      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} brand={tenant?.brand_name} />
      <PrivacyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} brand={tenant?.brand_name} />
    </div>
  );
}
